import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { selectUser } from "../features/auth/authSlice";
import { findMatchingPath } from "./helper";

// ---------------------------------------------------------------------------
// One rule for "is this row a real tab?" (2026-09-16)
//
// A menu's `modulesOfSubModule` rows are not all tabs. Some exist only to be
// granted: the API gates under Bank Management, the screens that are reachable
// by their own route rather than as a tab of their parent, and the dashboard
// headings. Screens used to map over the rows blindly, so those appeared as
// tab pills that open a blank pane, and — worse — they shifted the INDEX that
// every later tab uses to read its own rights.
//
// The marker is an ANCHOR in the path ("/FundManagement#pending"): an anchor is
// a part of a page, never a destination. Sidebar.js already drops them from the
// menu for the same reason.
//
// Grantable in the Permissions screen, never drawn as a tab.
// ---------------------------------------------------------------------------
export const isPermissionOnlyNode = (node) => String(node?.path || "").includes("#");

/**
 * The rows a screen should render as tabs, in order.
 *
 * Use this for BOTH the tab strip and the `[activeTab]` lookup — they are
 * joined by position, so filtering one and not the other silently hands a tab
 * another row's rights.
 */
export const tabNodes = (menu) =>
  (menu?.modulesOfSubModule || []).filter((n) => !isPermissionOnlyNode(n) && !n?.parent_id);

/**
 * The tabs a role may actually SEE, each with its ORIGINAL position.
 *
 * Screens bind their tab components to the strip by position
 * (`tabNodes(menu)[activeTab]`), so a hidden tab must not shift the others:
 * the strip draws only these, but every link still carries the tab's real
 * index. (2026-09-18: a role used to see every tab of a menu it could open,
 * with an empty pane behind the ones it had no right to.)
 */
export const visibleTabs = (menu) =>
  tabNodes(menu)
    .map((node, index) => ({ node, index }))
    .filter(({ node }) => Number(node.view) > 0);

/** Index of the first tab the role may see (0 when nothing is granted yet). */
export const firstVisibleTab = (menu) => {
  const v = visibleTabs(menu);
  return v.length ? v[0].index : 0;
};

/** Whether the tab at `index` (a ?tab= value) is one the role may see. */
export const isTabVisible = (menu, index) =>
  visibleTabs(menu).some((t) => String(t.index) === String(index));

/**
 * A row that belongs to a page section rather than the tab strip, found by the
 * slug after the "#". Returns undefined when it has not been seeded, and every
 * caller treats that as "show it" so an un-seeded environment is unchanged.
 */
export const sectionNode = (menu, slug) => {
  const tabs = menu?.modulesOfSubModule || [];
  // A sub-tab (Pending / Approved inside Fund Request) is nested under its tab,
  // so search both levels.
  const everything = tabs.concat(...tabs.map((t) => t.subTabs || []));
  return everything.find((n) => String(n?.path || "").split("#")[1] === slug);
};

/** The strip inside one tab, if that tab has one. */
export const subTabs = (tab) => tab?.subTabs || [];

// ---------------------------------------------------------------------------
// Action-level rights (2026-09-17).
//
// A node the server has catalogued carries `actions` — { reject: 1, transfer:
// 0, ... } — one entry per button that is not one of the five flags. A screen
// asks `canAct(node, "reject")` instead of reading `node.approve`.
//
// Fails to the OLD behaviour, never closed: a node without `actions` (an
// environment where the catalogue has not been seeded) answers with the flag
// the button always read, listed here, so nothing changes until the catalogue
// exists. A caller can pass its own `fallback` when the old gate was more
// specific than one flag.
// ---------------------------------------------------------------------------
const LEGACY_FLAG = {
  reject: "approve",
  reactivate: "update",
  transfer: "approve",
  revert: "approve",
  punch: null, // was never gated
  allocate: "delete",
  update_allocate: "update",
  hold: "update",
  update_status: "update",
  rework: "update",
  breakdown: "view",
};

export const canAct = (node, key, fallback) => {
  if (node?.actions && key in node.actions) return Number(node.actions[key]) > 0;
  if (fallback !== undefined) return !!fallback;
  const flag = LEGACY_FLAG[key];
  return flag ? Number(node?.[flag]) > 0 : true;
};

/**
 * The action gate for the tabs INSIDE one tab — the counterpart of
 * useInnerTabGate for buttons:
 *     const canActOn = useActionGate("/FundManagement");
 *     canActOn("fund-request-pending", "reject")
 * Looks the sub-tab up by its anchor slug; a slug with no row falls back the
 * same way canAct does (the `fallback`, else the legacy flag on `fallbackNode`).
 */
export const useActionGate = (menuPath) => {
  const { userPermission } = useSelector(selectUser);
  const { pathname } = useLocation();
  // A component with two homes (Expense Punch under Fund Management AND
  // Expense Management) asks the menu it is actually open under first, so a
  // right granted on one home is not answered by the other's row.
  const paths = (Array.isArray(menuPath) ? menuPath : [menuPath])
    .slice()
    .sort((a, b) => (pathname.startsWith(b) ? 1 : 0) - (pathname.startsWith(a) ? 1 : 0));
  const menus = paths.map((p) => findMatchingPath(userPermission, p));
  return (slug, key, fallback, fallbackNode) => {
    const owner = menus.find((m) => sectionNode(m, slug));
    const node = owner ? sectionNode(owner, slug) : null;
    if (node && node.actions && key in node.actions) return Number(node.actions[key]) > 0;
    return canAct(fallbackNode || node, key, fallback);
  };
};

/**
 * One of the five flags read from a SUB-TAB row (Pending / Approved inside
 * Fund Request) instead of the tab above it. The Permissions screen offers
 * Edit / Approve on the sub-tab rows now, so a button on that strip must ask
 * the sub-tab. The sidebar has already folded the sub-tab against its tab and
 * menu, so the answer is the effective right. A slug with no row falls back
 * to `fallback` (the tab's flag, as before).
 */
export const useFlagGate = (menuPath) => {
  const { userPermission } = useSelector(selectUser);
  const { pathname } = useLocation();
  const paths = (Array.isArray(menuPath) ? menuPath : [menuPath])
    .slice()
    .sort((a, b) => (pathname.startsWith(b) ? 1 : 0) - (pathname.startsWith(a) ? 1 : 0));
  const menus = paths.map((p) => findMatchingPath(userPermission, p));
  return (slug, flag, fallback) => {
    const owner = menus.find((m) => sectionNode(m, slug));
    const node = owner ? sectionNode(owner, slug) : null;
    return node ? Number(node[flag]) > 0 : !!fallback;
  };
};

/**
 * Inner tab strips (react-best-tabs) number their tabs by POSITION, so a tab
 * the role may not see cannot simply be left out — the ones after it would
 * shift. It is hidden with "d-none" instead (2026-09-18; it used to be dimmed
 * but still shown), and when the URL points at a hidden tab the screen jumps
 * to the first one the role may see.
 *
 *     const inner = useInnerTabs("/FundManagement", { 2: "fund-request-pending", 3: "fund-request-approved" });
 *     <Tab className={inner.cls("fund-request-pending")} ...>
 *
 * `tabsByParam` maps the value the URL carries (?subtab=2, ?ltab=approved ...)
 * to that tab's slug. Fails open like everything here: an unseeded slug counts
 * as visible.
 */
export const useInnerTabs = (menuPath, tabsByParam, { param = "subtab", fallback } = {}) => {
  const canSee = useInnerTabGate(menuPath);
  const [searchParams, setSearchParams] = useSearchParams();
  const visible = Object.keys(tabsByParam).filter((k) => canSee(tabsByParam[k]));
  const current = searchParams.get(param) ?? (fallback == null ? Object.keys(tabsByParam)[0] : String(fallback));
  const visibleKey = visible.join(",");
  useEffect(() => {
    if (!visible.length || visible.includes(String(current))) return;
    const next = new URLSearchParams(searchParams);
    next.set(param, visible[0]);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, visibleKey]);
  return { canSee, cls: (slug) => (canSee(slug) ? "" : "d-none"), visible };
};

/**
 * Same as useInnerTabs for screens that keep the active tab in local state
 * (`useState`) rather than the URL: hides the pills the role may not see and
 * moves the state onto the first visible tab when it points at a hidden one.
 */
export const useInnerTabState = (menuPath, tabsByIndex, activeTab, setActiveTab) => {
  const canSee = useInnerTabGate(menuPath);
  const visible = Object.keys(tabsByIndex).filter((k) => canSee(tabsByIndex[k]));
  const visibleKey = visible.join(",");
  useEffect(() => {
    if (!visible.length || visible.includes(String(activeTab))) return;
    setActiveTab(typeof activeTab === "number" ? Number(visible[0]) : String(visible[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, visibleKey]);
  return { canSee, cls: (slug) => (canSee(slug) ? "" : "d-none"), visible };
};

/** Fails OPEN: no row seeded for this section yet means it stays visible. */
export const canSeeSection = (menu, slug) => {
  const node = sectionNode(menu, slug);
  return node ? Number(node.view) > 0 : true;
};

/**
 * The gate for the tabs INSIDE one tab, e.g. Pending / Approved / Rejected
 * within Fund Request.
 *
 * Self-contained on purpose: a screen only needs
 *     const canSeeInner = useInnerTabGate("/FundManagement");
 * and then `canSeeInner("fund-request-approved")`. Doing the redux read here
 * keeps ~30 screens from each growing their own copy of the same three lines,
 * and there is exactly one place to change if the tree moves.
 *
 * Fails OPEN, like canSeeSection: an inner tab with no row stays visible, so a
 * database that has not been seeded behaves exactly as it does today.
 */
export const useInnerTabGate = (menuPath) => {
  const { userPermission } = useSelector(selectUser);
  // A component can be a tab of MORE than one screen — Expense Punch is a tab of
  // both Fund Management and Expense Management — so a list of menus is allowed.
  // The rows are seeded under each, and whichever holds this slug answers.
  const paths = Array.isArray(menuPath) ? menuPath : [menuPath];
  const menus = paths.map((p) => findMatchingPath(userPermission, p));
  return (slug) => {
    const owner = menus.find((m) => sectionNode(m, slug));
    // Fails open, as everywhere else: no row seeded means the tab stays visible.
    return owner ? canSeeSection(owner, slug) : true;
  };
};
